/**
 * Copyright (c) 2025 Répét Contributors
 * Licensed under the MIT License
 * See LICENSE file in the project root for full license text
 */

import { useState, useRef } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuthStore } from '../state/authStore'
import { Layout } from '../components/common/Layout'
import { Header } from '../components/common/Header'
import { APP_NAME } from '../config/env'

type Step = 'email' | 'verify' | 'success'

/**
 * Écran "Mot de passe oublié" — flux 3 étapes sur un seul écran :
 * 1. Email → bouton envoi code
 * 2. Saisie du code (6 chiffres) + nouveau mot de passe
 * 3. Succès → redirection login
 */
export function ForgotPasswordScreen() {
  const navigate = useNavigate()
  const { forgotPassword, resetPassword, isLoading } = useAuthStore()

  const [step, setStep] = useState<Step>('email')
  const [email, setEmail] = useState('')
  const [code, setCode] = useState<string[]>(['', '', '', '', '', ''])
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [resendCooldown, setResendCooldown] = useState(0)

  const codeRefs = useRef<(HTMLInputElement | null)[]>([])

  // ── Cooldown renvoi ────────────────────────────────────────────────────────
  const startResendCooldown = () => {
    setResendCooldown(60)
    const interval = setInterval(() => {
      setResendCooldown((prev) => {
        if (prev <= 1) { clearInterval(interval); return 0 }
        return prev - 1
      })
    }, 1000)
  }

  // ── Étape 1 ───────────────────────────────────────────────────────────────
  const handleSendCode = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    try {
      await forgotPassword(email)
      setStep('verify')
      startResendCooldown()
      setTimeout(() => codeRefs.current[0]?.focus(), 50)
    } catch {
      setError('Une erreur est survenue. Veuillez réessayer.')
    }
  }

  const handleResend = async () => {
    if (resendCooldown > 0 || isLoading) return
    setError(null)
    try {
      await forgotPassword(email)
      setCode(['', '', '', '', '', ''])
      startResendCooldown()
      setTimeout(() => codeRefs.current[0]?.focus(), 50)
    } catch {
      setError('Erreur lors du renvoi. Veuillez réessayer.')
    }
  }

  // ── Étape 2 : saisie code ────────────────────────────────────────────────
  const handleCodeInput = (index: number, value: string) => {
    const digit = value.replace(/\D/g, '').slice(-1)
    const next = [...code]
    next[index] = digit
    setCode(next)
    if (digit && index < 5) codeRefs.current[index + 1]?.focus()
  }

  const handleCodeKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace') {
      if (code[index]) {
        const next = [...code]; next[index] = ''; setCode(next)
      } else if (index > 0) {
        codeRefs.current[index - 1]?.focus()
      }
    } else if (e.key === 'ArrowLeft' && index > 0) {
      codeRefs.current[index - 1]?.focus()
    } else if (e.key === 'ArrowRight' && index < 5) {
      codeRefs.current[index + 1]?.focus()
    }
  }

  const handleCodePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault()
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6)
    if (!pasted) return
    const next: string[] = ['', '', '', '', '', '']
    for (let i = 0; i < 6; i++) next[i] = pasted[i] ?? ''
    setCode(next)
    codeRefs.current[Math.min(pasted.length, 5)]?.focus()
  }

  // ── Étape 2 : soumission reset ───────────────────────────────────────────
  const handleReset = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    const fullCode = code.join('')
    if (fullCode.length !== 6 || code.some((d) => d === '')) {
      setError('Veuillez saisir le code à 6 chiffres complet.')
      return
    }
    if (newPassword.length < 8) {
      setError('Le mot de passe doit contenir au moins 8 caractères.')
      return
    }
    if (newPassword !== confirmPassword) {
      setError('Les mots de passe ne correspondent pas.')
      return
    }

    try {
      await resetPassword(email, fullCode, newPassword)
      setStep('success')
      setTimeout(() => navigate('/login'), 4000)
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Code invalide ou expiré.'
      setError(msg)
      // Réinitialiser le code pour resaisie
      setCode(['', '', '', '', '', ''])
      setTimeout(() => codeRefs.current[0]?.focus(), 50)
    }
  }

  // ── Shared UI atoms ───────────────────────────────────────────────────────
  const SpinnerIcon = () => (
    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
    </svg>
  )

  const btnClass = 'group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed'
  const inputClass = 'appearance-none relative block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm bg-white dark:bg-gray-800'

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <Layout header={<Header title={APP_NAME} showLogo />}>
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">

          {/* ── Étape 1 : email ── */}
          {step === 'email' && (
            <>
              <div>
                <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900 dark:text-white">
                  Mot de passe oublié
                </h2>
                <p className="mt-2 text-center text-sm text-gray-600 dark:text-gray-400">
                  Saisissez votre adresse email pour recevoir un code de réinitialisation.
                </p>
              </div>

              <form className="mt-8 space-y-6" onSubmit={handleSendCode}>
                {error && (
                  <div className="rounded-md bg-red-50 dark:bg-red-900/20 p-4">
                    <p className="text-sm font-medium text-red-800 dark:text-red-200">{error}</p>
                  </div>
                )}

                <div>
                  <label htmlFor="email" className="sr-only">Email</label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className={inputClass}
                    placeholder="Votre adresse email"
                    disabled={isLoading}
                  />
                </div>

                <div>
                  <button type="submit" disabled={isLoading} className={btnClass}>
                    {isLoading
                      ? <span className="flex items-center"><SpinnerIcon />Envoi en cours...</span>
                      : 'Envoyer un code par email'
                    }
                  </button>
                </div>

                <div className="text-center">
                  <Link to="/login" className="font-medium text-blue-600 hover:text-blue-500 dark:text-blue-400 dark:hover:text-blue-300 text-sm">
                    Retour à la connexion
                  </Link>
                </div>
              </form>
            </>
          )}

          {/* ── Étape 2 : code + nouveau mot de passe ── */}
          {step === 'verify' && (
            <>
              <div>
                <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900 dark:text-white">
                  Nouveau mot de passe
                </h2>
                <p className="mt-2 text-center text-sm text-gray-600 dark:text-gray-400">
                  Un code à 6 chiffres a été envoyé à <strong>{email}</strong>.
                  Saisissez-le ci-dessous avec votre nouveau mot de passe.
                </p>
              </div>

              <form className="mt-8 space-y-6" onSubmit={handleReset}>
                {error && (
                  <div className="rounded-md bg-red-50 dark:bg-red-900/20 p-4">
                    <p className="text-sm font-medium text-red-800 dark:text-red-200">{error}</p>
                  </div>
                )}

                {/* Inputs code à 6 chiffres */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3 text-center">
                    Code reçu par email
                  </label>
                  <div className="flex justify-center gap-2">
                    {code.map((digit, i) => (
                      <input
                        key={i}
                        ref={(el) => { codeRefs.current[i] = el }}
                        type="text"
                        inputMode="numeric"
                        maxLength={1}
                        value={digit}
                        onChange={(e) => handleCodeInput(i, e.target.value)}
                        onKeyDown={(e) => handleCodeKeyDown(i, e)}
                        onPaste={i === 0 ? handleCodePaste : undefined}
                        disabled={isLoading}
                        className="w-11 h-14 text-center text-2xl font-bold border-2 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50
                          border-gray-300 dark:border-gray-600 transition-colors"
                        aria-label={`Chiffre ${i + 1} du code`}
                      />
                    ))}
                  </div>

                  {/* Renvoi */}
                  <div className="mt-3 text-center">
                    <button
                      type="button"
                      onClick={handleResend}
                      disabled={resendCooldown > 0 || isLoading}
                      className="text-sm text-blue-600 dark:text-blue-400 hover:underline disabled:opacity-50 disabled:no-underline disabled:cursor-not-allowed"
                    >
                      {resendCooldown > 0
                        ? `Renvoyer un code (${resendCooldown}s)`
                        : 'Renvoyer un code'
                      }
                    </button>
                  </div>
                </div>

                {/* Nouveau mot de passe */}
                <div className="space-y-3">
                  <div>
                    <label htmlFor="new-password" className="sr-only">Nouveau mot de passe</label>
                    <input
                      id="new-password"
                      name="new-password"
                      type="password"
                      autoComplete="new-password"
                      required
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className={inputClass}
                      placeholder="Nouveau mot de passe (8 caractères min.)"
                      disabled={isLoading}
                    />
                  </div>
                  <div>
                    <label htmlFor="confirm-password" className="sr-only">Confirmer le mot de passe</label>
                    <input
                      id="confirm-password"
                      name="confirm-password"
                      type="password"
                      autoComplete="new-password"
                      required
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className={inputClass}
                      placeholder="Confirmer le nouveau mot de passe"
                      disabled={isLoading}
                    />
                  </div>
                </div>

                <div>
                  <button type="submit" disabled={isLoading} className={btnClass}>
                    {isLoading
                      ? <span className="flex items-center"><SpinnerIcon />Réinitialisation...</span>
                      : 'Réinitialiser mon mot de passe'
                    }
                  </button>
                </div>

                <div className="text-center">
                  <button
                    type="button"
                    onClick={() => { setStep('email'); setError(null); setCode(['', '', '', '', '', '']) }}
                    className="text-sm text-gray-500 dark:text-gray-400 hover:underline"
                  >
                    ← Modifier l'adresse email
                  </button>
                </div>
              </form>
            </>
          )}

          {/* ── Étape 3 : succès ── */}
          {step === 'success' && (
            <div className="rounded-md bg-green-50 dark:bg-green-900/20 p-6 text-center space-y-4">
              <svg className="mx-auto h-12 w-12 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-sm font-medium text-green-800 dark:text-green-200">
                Mot de passe réinitialisé avec succès&nbsp;!
              </p>
              <p className="text-xs text-green-700 dark:text-green-300">
                Redirection vers la connexion dans quelques secondes...
              </p>
              <div className="pt-2">
                <Link to="/login" className="font-medium text-blue-600 hover:text-blue-500 dark:text-blue-400 dark:hover:text-blue-300 text-sm">
                  Se connecter maintenant
                </Link>
              </div>
            </div>
          )}

        </div>
      </div>
    </Layout>
  )
}
